import { type CSSProperties, lazy, type ReactElement, Suspense } from 'react';
import type { Control, ControllerRenderProps, FieldError, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { SDSFlexboxGap } from '@repo/sds/SDSFlexbox';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import type { BaseFormCheckboxInputProps } from '@repo/ui/fixtures/form/components/FormCheckboxInput/FormCheckboxInput';
import { FormCheckboxInput } from '@repo/ui/fixtures/form/components/FormCheckboxInput/FormCheckboxInput';
import type { BaseFormDateInputProps } from '@repo/ui/fixtures/form/components/FormDateInput/FormDateInput';
import type { BaseFormFileInputProps } from '@repo/ui/fixtures/form/components/FormFileInput/FormFileInput';
import type { BaseFormImageInputProps } from '@repo/ui/fixtures/form/components/FormImageInput/FormImageInput';
import type { BaseFormQuerySelectProps } from '@repo/ui/fixtures/form/components/FormQuerySelect/FormQuerySelect';
import { FormQuerySelect } from '@repo/ui/fixtures/form/components/FormQuerySelect/FormQuerySelect';
import type { BaseFormRichTextInputProps } from '@repo/ui/fixtures/form/components/FormRichTextInput/FormRichTextInput';
import type { BaseFormSelectInputProps } from '@repo/ui/fixtures/form/components/FormSelectInput/FormSelectInput';
import { FormSelectInput } from '@repo/ui/fixtures/form/components/FormSelectInput/FormSelectInput';
import type { BaseFormSliderInputProps } from '@repo/ui/fixtures/form/components/FormSliderInput/FormSliderInput';
import { FormSliderInput } from '@repo/ui/fixtures/form/components/FormSliderInput/FormSliderInput';
import type { BaseFormSwitchInputProps } from '@repo/ui/fixtures/form/components/FormSwitchInput/FormSwitchInput';
import { FormSwitchInput } from '@repo/ui/fixtures/form/components/FormSwitchInput/FormSwitchInput';
import type { BaseFormTextInputProps } from '@repo/ui/fixtures/form/components/FormTextInput/FormTextInput';
import { FormTextInput } from '@repo/ui/fixtures/form/components/FormTextInput/FormTextInput';

import type { BaseFormTextAreaInputProps } from '../FormTextAreaInput/FormTextAreaInput';
import { FormTextAreaInput } from '../FormTextAreaInput/FormTextAreaInput';
import type { BaseFormTimeDropdownInputProps } from '../FormTimeDropdownInput/FormTimeDropdownInput';
import type { BaseFormTimeInputProps } from '../FormTimeInput/FormTimeInput';
import type { BaseFormTimezoneInputProps } from '../FormTimezoneInput/FormTimezoneInput';
import type { HeavyFormFieldComponents } from './heavy-form-fields';

// Field types that pull in heavy dependencies (TipTap, calendars, time zones, uploads) load on first use, so forms
// built only from light inputs, such as the landing contact form, do not ship them.
const LazyHeavyFormFields = lazy(() =>
  import('./heavy-form-fields').then(module => ({ default: module.HeavyFormFields })),
);

function renderHeavyFormField(render: (fields: HeavyFormFieldComponents) => ReactElement): ReactElement {
  return (
    <Suspense fallback={null}>
      <LazyHeavyFormFields render={render} />
    </Suspense>
  );
}

// Component mapping for form field types

// Helper function to build controlled props
function buildControlledProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  field: ControllerRenderProps<TFieldValues, TName>,
  fieldState: { error?: FieldError },
  formState: { isSubmitting: boolean },
  isParentDisabled: boolean,
  control?: Control<TFieldValues, unknown, FieldValues>,
): ControlledFieldProps<TFieldValues, TName> {
  const isDisabled = isParentDisabled || formState.isSubmitting;
  const fieldError: FieldError | undefined = fieldState.error;
  const errorMessage = fieldError?.message?.toString();

  return {
    control,
    field,
    errorMessage,
    isDisabled,
  };
}

// Helper function to render the appropriate form component
function renderFormComponent<TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  fieldConfig: FormFieldConfig<TFieldValues>,
  controlledProps: ControlledFieldProps<TFieldValues, TName>,
): React.ReactElement {
  switch (fieldConfig.componentType) {
    case 'FormCheckboxInput':
      return <FormCheckboxInput {...fieldConfig.componentProps} {...controlledProps} />;
    case 'FormDateInput': {
      const { componentProps } = fieldConfig;
      return renderHeavyFormField(({ FormDateInput }) => <FormDateInput {...componentProps} {...controlledProps} />);
    }
    case 'FormSelectInput':
      return <FormSelectInput {...fieldConfig.componentProps} {...controlledProps} />;
    case 'FormQuerySelect':
      return <FormQuerySelect {...fieldConfig.componentProps} {...controlledProps} />;
    case 'FormTextAreaInput':
      return <FormTextAreaInput {...fieldConfig.componentProps} {...controlledProps} />;
    case 'FormTextInput':
      return <FormTextInput {...fieldConfig.componentProps} {...controlledProps} />;
    case 'FormRichTextInput': {
      const { componentProps } = fieldConfig;
      return renderHeavyFormField(({ FormRichTextInput }) => (
        <FormRichTextInput {...componentProps} {...controlledProps} />
      ));
    }
    case 'FormFileInput': {
      const { componentProps } = fieldConfig;
      return renderHeavyFormField(({ FormFileInput }) => <FormFileInput {...componentProps} {...controlledProps} />);
    }
    case 'FormImageInput': {
      const { componentProps } = fieldConfig;
      return renderHeavyFormField(({ FormImageInput }) => <FormImageInput {...componentProps} {...controlledProps} />);
    }
    case 'FormTimeInput': {
      const { componentProps } = fieldConfig;
      return renderHeavyFormField(({ FormTimeInput }) => <FormTimeInput {...componentProps} {...controlledProps} />);
    }
    case 'FormTimeDropdownInput': {
      const { componentProps } = fieldConfig;
      return renderHeavyFormField(({ FormTimeDropdownInput }) => (
        <FormTimeDropdownInput {...componentProps} {...controlledProps} />
      ));
    }
    case 'FormTimezoneInput': {
      const { componentProps } = fieldConfig;
      return renderHeavyFormField(({ FormTimezoneInput }) => (
        <FormTimezoneInput {...componentProps} {...controlledProps} />
      ));
    }
    case 'FormSliderInput':
      return <FormSliderInput {...fieldConfig.componentProps} {...controlledProps} />;
    case 'FormSwitchInput':
      return <FormSwitchInput {...fieldConfig.componentProps} {...controlledProps} />;
    case 'FormCustomField':
      return fieldConfig.render(controlledProps);
    default:
      throw new Error(`Invalid component type provided to FormField`);
  }
}

/** Core props provided by the Controller wrapper to the leaf UI input component. */
export interface ControlledFieldProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> {
  /** The form control object from RHF, used by advanced custom fields. */
  control?: Control<TFieldValues, unknown, FieldValues>;
  /** The field object from RHF Controller (value, onChange, onBlur, ref, name). */
  field: ControllerRenderProps<TFieldValues, TName>;
  /** Extracted error message string for the field, if any. */
  errorMessage: string | undefined;
  /** Final calculated disabled state for the input. */
  isDisabled: boolean;
}

/** Optional layout properties applicable to any form field. */
interface FormFieldLayoutProps {
  width?: string;
}

/** Base props for custom field render function. */
export interface BaseFormCustomFieldProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
}

/** Render function type for custom fields. */
export type FormCustomFieldRender<TFieldValues extends FieldValues> = <TName extends Path<TFieldValues>>(
  props: ControlledFieldProps<TFieldValues, TName>,
) => ReactElement;

/**
 * Union type defining all possible field configurations, mapping a
 * component type string to its specific base props interface.
 */
export type FormFieldConfig<TFieldValues extends FieldValues> =
  | ({
      componentType: 'FormCheckboxInput';
      componentProps: BaseFormCheckboxInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({ componentType: 'FormDateInput'; componentProps: BaseFormDateInputProps<TFieldValues> } & FormFieldLayoutProps)
  | ({
      componentType: 'FormSelectInput';
      componentProps: BaseFormSelectInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({
      componentType: 'FormQuerySelect';
      componentProps: BaseFormQuerySelectProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({
      componentType: 'FormTextAreaInput';
      componentProps: BaseFormTextAreaInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({ componentType: 'FormTextInput'; componentProps: BaseFormTextInputProps<TFieldValues> } & FormFieldLayoutProps)
  | ({
      componentType: 'FormRichTextInput';
      componentProps: BaseFormRichTextInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({ componentType: 'FormFileInput'; componentProps: BaseFormFileInputProps<TFieldValues> } & FormFieldLayoutProps)
  | ({ componentType: 'FormImageInput'; componentProps: BaseFormImageInputProps<TFieldValues> } & FormFieldLayoutProps)
  | ({ componentType: 'FormTimeInput'; componentProps: BaseFormTimeInputProps<TFieldValues> } & FormFieldLayoutProps)
  | ({
      componentType: 'FormTimeDropdownInput';
      componentProps: BaseFormTimeDropdownInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({
      componentType: 'FormTimezoneInput';
      componentProps: BaseFormTimezoneInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({
      componentType: 'FormSliderInput';
      componentProps: BaseFormSliderInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({
      componentType: 'FormSwitchInput';
      componentProps: BaseFormSwitchInputProps<TFieldValues>;
    } & FormFieldLayoutProps)
  | ({
      componentType: 'FormCustomField';
      componentProps: BaseFormCustomFieldProps<TFieldValues>;
      /** Render function that receives controlled props and returns a React element. */
      render: FormCustomFieldRender<TFieldValues>;
    } & FormFieldLayoutProps);

/** Props for the internal BaseFormField component which wraps the RHF Controller. */
interface BaseFormFieldProps<
  TFieldValues extends FieldValues,
  TContext,
  TOutputType extends FieldValues = TFieldValues,
> {
  fieldConfig: FormFieldConfig<TFieldValues>;
  control: Control<TFieldValues, TContext, TOutputType>;
  isDisabled: boolean; // General disabled state from parent.
}

/**
 * Internal component acting as the RHF Controller wrapper. It handles controller logic
 * and dispatches rendering to the appropriate leaf input component.
 */
function BaseFormField<TFieldValues extends FieldValues, TContext, TOutputType extends FieldValues = TFieldValues>({
  fieldConfig,
  control,
  isDisabled: isParentDisabled,
}: BaseFormFieldProps<TFieldValues, TContext, TOutputType>) {
  const { name } = fieldConfig.componentProps;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState, formState }) => {
        const controlledProps = buildControlledProps(
          field,
          fieldState,
          formState,
          isParentDisabled,
          control as Control<TFieldValues, unknown, FieldValues>,
        );
        return renderFormComponent(fieldConfig, controlledProps);
      }}
    />
  );
}

/** Props for the exported FormField component wrapper. */
interface FormFieldProps<
  TFieldValues extends FieldValues,
  TContext, // Inherits validation context type from Form.
  TOutputType extends FieldValues = TFieldValues, // Inherits Zod output type from Form.
> {
  /** The configuration object for this specific field. */
  fieldConfig: FormFieldConfig<TFieldValues>;
  /** The react-hook-form control object. */
  control: Control<TFieldValues, TContext, TOutputType>;
  /** Flag indicating if the field should be disabled. */
  isDisabled: boolean;
  /** CSS gap value passed from parent, used for width calculations. */
  gap: SDSFlexboxGap;
}

/** Width and flex behaviour of a field in a wrapping row, so content placed beside fields lines up with them. */
export function getFormFieldLayoutStyle(width: string, gap: SDSFlexboxGap): CSSProperties {
  return {
    flex: width ? '1 1 auto' : '1',
    minWidth: 0,
    width: width === '100%' ? '100%' : `calc(${width} - var(--gaps-${gap}) / 2)`,
    maxWidth: width === '100%' ? '100%' : `calc(${width} - var(--gaps-${gap}) / 2)`,
  };
}

/**
 * Renders a single form field within a layout container, responsible for
 * calculating field width and delegating RHF integration and specific
 * input rendering to the internal BaseFormField component.
 */
export function FormField<TFieldValues extends FieldValues, TContext, TOutputType extends FieldValues = TFieldValues>({
  fieldConfig,
  control,
  isDisabled,
  gap,
}: FormFieldProps<TFieldValues, TContext, TOutputType>) {
  const { width = '100%' } = fieldConfig;

  // Error message extraction and passing is now handled within BaseFormField.

  return (
    <SDSFlexbox style={getFormFieldLayoutStyle(width, gap)}>
      <BaseFormField fieldConfig={fieldConfig} control={control} isDisabled={isDisabled} />
    </SDSFlexbox>
  );
}
